import { getCenter, getHeight, getTopLeft, getWidth } from 'ol/extent';
import { get as getProjection } from 'ol/proj';
import TileGrid from 'ol/tilegrid/TileGrid';

import { OAuth2Creds } from 'models/auth';
import { AuthManager, Loadable } from 'models';
import { MAP_PROJECTION } from 'config';
import { AbstractLayer } from 'controllers/AbstractLayer';

export const sortLayersByOrderPosition = (layers: AbstractLayer[]) =>
    [...layers].sort((a, b) => {
        const posA = a.getOrderPosition();
        const posB = b.getOrderPosition();
        if (posA == null) return 1;
        if (posB == null) return -1;
        return posA > posB ? -1 : 1;
    });

export const helpLookup: Record<string, { title: string; text: string }> = {};

/**
 * An awaitable wrapper around the `setTimeout` function.
 * @param ms The milliseconds to sleep for
 * @returns A Promise that resolves when the time has elapsed
 */
export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Changes the first letter of a string to an uppercase letter
 * @param str The string to capitalize
 * @returns The original string with the first letter capitalised
 */
export const capitalize = (str?: string | null): string | null => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : null;
};

/**
 * Converts a string to Title Case (capitalizes the first letter of each word).
 *
 * Example: "sample text" -> "Sample Text"
 *
 * @param str - The input string to convert
 * @returns The converted string in Title Case
 */
export const toTitleCase = (str: string): string => {
    return str.replace(
        /\w\S*/g,
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    );
};

/**
 * Will convert an object into URLSearchParams
 * @param obj An object containing the data to convert
 * @param defaults Default values to use if they don't exist within obj
 * @param cloneFrom An optional existing URLSearchParams to retain the existing values of
 * @returns A URLSearchParams instance containing data from the object
 */
export function createURLSearchParams(
    obj: Record<
        string,
        | string
        | number
        | boolean
        | Date
        | number[]
        | string[]
        | null
        | undefined
    >,
    defaults?: Record<string, string | number | boolean | Date>,
    cloneFrom?: URLSearchParams,
) {
    const params = new URLSearchParams(cloneFrom);

    Object.keys({
        ...defaults,
        ...obj,
    }).forEach((key) => {
        let value = obj[key];

        if (value == null) {
            if (defaults?.[key]) {
                value = defaults?.[key];
            } else {
                return;
            }
        }

        if (value instanceof Date) {
            params.append(key, value.toISOString());
        } else {
            params.append(key, `${value}`);
        }
    });
    return params;
}

/**
 * Check if something is an object.
 * @param item Item to check
 * @returns True if the item is an object. Enforces the type of the input
 */
export function isObject(item: any): item is Record<string, any> {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Will deeply merge two objects
 *
 * Primarily needed by tests when merging
 * Default redux state with any test specific state
 *
 * Algorithm 'heavily inspired' by
 * https://stackoverflow.com/a/37164538/12757511
 *
 */
export function mergeDeep<
    T extends Record<string, any>,
    S extends Record<string, any>,
>(target: T, source?: S): T {
    if (!isObject(target)) return (source ?? target) as unknown as T;
    if (!isObject(source)) return target;

    const output = Object.assign({}, target) as T;
    Object.keys(source).forEach((key) => {
        if (isObject(source[key])) {
            if (!(key in target) || !isObject(target[key])) {
                Object.assign(output, { [key]: source[key] });
            } else {
                // @ts-ignore
                output[key] = mergeDeep(target[key], source[key]);
            }
        } else {
            Object.assign(output, { [key]: source[key] });
        }
    });
    return output;
}

/**
 * Check if a set of credentials has a group
 * @param group The group to check for
 * @param auth The OAuth creds to check
 * @returns True if the credentials contains the provided group, false otherwise.
 */
export const hasGroup = (
    group: AuthManager.UserGroups,
    auth: Loadable<OAuth2Creds>,
): boolean => {
    if (auth.object?.decodedJWT.cognitoGroups)
        return auth.object?.decodedJWT.cognitoGroups?.indexOf(group) > -1;
    return false;
};

/**
 * Check if a user has a particular role
 * @param user The user to check
 * @param roles The roles to look for
 * @returns True if the user contains any one of the provided roles. False otherwise
 */
export const hasRole = (
    user: { roles?: string[] } | null | undefined,
    ...roles: (
        | 'Project Manager'
        | 'Project Admin'
        | 'BMC Support'
        | 'BMC Executive Officer'
        | 'BMC Member'
    )[]
) => {
    if (!user || !user.roles) return false;

    return !!roles.find((role) => user.roles?.includes(role));
};

/**
 * Get a deeply nested property from an object
 * @param path A list of strings representing the object property path to the property
 * @param obj The object to search
 * @returns The property or null if not found.
 */
export const getProperty = (
    path: (string | number)[],
    obj: Record<string | number, any>,
) =>
    path.reduce<any>(
        (record, prop) => (record && record[prop] ? record[prop] : null),
        obj,
    );

/**
 * Get a Users Current Coordinates
 * @returns A Promise that will resolve to the users coordinates or reject if the user
 * doesn't allow coords or it's not supported
 */
export const getCoordinates = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
    });
};

/**
 * @param deg Degrees, in degrees (like the circle degrees)
 * @returns The radians represented by the provided degrees
 */
export const deg2rad = (deg: number) => deg * (Math.PI / 180);

/**
 * @param coords The coords to print in the form [longitude, latitude]
 * @returns A pretty string represeting the coordinates
 */
export const prettifyLatLng = (coords: number[]) => {
    const [longitude, latitude] = coords;
    const latDir = latitude > 0 ? 'N' : 'S';
    const lngDir = longitude < 0 ? 'W' : 'E';

    const values = [latitude, longitude].map(Math.abs).map((x) => x.toFixed(5));

    return `${values[0]}°${latDir}, ${values[1]}°${lngDir}`;
};

/**
 * Get the approximate distance between two point (longitude/latitude)
 * @returns The distance in meters between two points using the great circle approcimation
 */
export const haversineDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1); // deg2rad below
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) *
            Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c * 1000; // Distance in m
    return d;
};

/**
 * Not intended to be crypographically strong and is intentally small
 * so not to be used for large datasets.
 * Intended for random Id's for generated frontend content.
 * @returns A random 8 character string
 */
export const makeWeakRandomId = () =>
    Math.random().toString(36).substring(2, 8);

/**
 * Will round the given number to the given number of decimal places
 * @param value The number to round
 * @param decimals The number of decimal places to round to (default: 0)
 * @returns The rounded number
 */
export const round = (
    value: number | null | undefined,
    decimals = 0,
): number => {
    if (value == null) return NaN;
    // Makes this very quick for cases 0, 1, 2.
    // 3 and beyond are fast but not good for anything real time or for large datasets
    switch (decimals) {
        case 0:
            return Math.round(value);
        case 1:
            return Math.round(value * 10) / 10;
        case 2:
            return Math.round(value * 100) / 100;
        default:
    }
    const pow = 10 ** decimals;
    return Math.round(value * pow) / pow;
};

/**
 * Will convert the distance in meters to a more readable string
 * eg: 1200 -> 1.2km
 * @param distance Raw distance in meters
 */
export const toReadableDistance = (
    distance: number | undefined | null,
): string | null => {
    if (distance == null) return null;
    if (distance < 0) return '0m';
    if (distance < 1000) return `${Math.round(distance)}m`;
    return `${Math.round(distance / 100) / 10}km`;
};

/**
 *
 * @param age The age in millisecionds. Negative age returns useful text, but not supported.
 */
export const toReadableAge = (
    age: number | undefined | null,
): string | null => {
    if (age == null) return null;
    if (age < 0) return "hasn't occured yet";
    if (age < 1000) return 'now';
    let time = Math.round(age / 1000);
    if (age < 60 * 1000) return `${time} second${time === 1 ? '' : 's'} ago`;
    time = Math.round(age / (1000 * 60));
    if (age < 60 * 60 * 1000)
        return `${time} minute${time === 1 ? '' : 's'} ago`;
    time = Math.round(age / (1000 * 60 * 60));
    if (age < 24 * 60 * 60 * 1000)
        return `${time} hour${time === 1 ? '' : 's'} ago`;
    time = Math.round(age / (1000 * 60 * 60 * 24));
    if (age < 7 * 24 * 60 * 60 * 1000)
        return `${time} day${time === 1 ? '' : 's'} ago`;
    time = Math.round(age / (1000 * 60 * 60 * 24 * 7));
    return `${time} week${time === 1 ? '' : 's'} ago`;
};

/**
 *
 * @param date The date to compare to from now
 */
export const toReadableTimeFromNow = (date?: Date | null): string => {
    if (!date) return 'n/a';
    /**
     * Positive values in the future, negative in the past
     */
    const signedAge = date.getTime() - Date.now();
    /**
     * abs age for easy comparisons
     */
    const age = Math.abs(signedAge);

    const isPast = signedAge < 0;

    const end = isPast ? ' ago' : '';
    const start = isPast ? '' : 'in ';

    if (age < 0) return "hasn't occured yet";
    if (age < 1000) return 'now';
    let time = Math.round(age / 1000);
    if (age < 60 * 1000)
        return `${start}${time} second${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60));
    if (age < 60 * 60 * 1000)
        return `${start}${time} minute${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60));
    if (age < 24 * 60 * 60 * 1000)
        return `${start}${time} hour${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60 * 24));
    if (age < 7 * 24 * 60 * 60 * 1000)
        return `${start}${time} day${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60 * 24 * 7));
    return `${start}${time} week${time === 1 ? '' : 's'}${end}`;
};

const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/**
 * @returns The date in a DD MMM format; Eg: '8 Jan'
 */
export const toDDMonFormat = (date: Date) => {
    return `${date.getDate()} ${months[date.getMonth()]}`;
};

/**
 * @returns The date in a DD/MM/YYYY format; Eg: '8/10/2022'
 */
export const toDDMMYYYYFormat = (date: Date) => {
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

/**
 * @returns The date in a HH:mm DD MMM YYYY format; Eg: '11:30 8 Jun 2022'
 */
export const toTimeDDMonYearFormat = (date: Date) => {
    return `${date.getHours() < 10 ? '0' : ''}${date.getHours()}:${
        date.getMinutes() < 10 ? '0' : ''
    }${date.getMinutes()} ${date.getDate()} ${
        months[date.getMonth()]
    } ${date.getFullYear()}`;
};

/**
 * @returns True if the provided date represents todays date. False otherwise
 */
export const isToday = (date: Date) =>
    new Date().toDateString() === date.toDateString();

/**
 * Will accept a user object to extract their full name from.
 * If name attributes are missing, it will return username. If all else fails, then it will return anonymous.
 * @param user The user object
 */
export const getFullName = <
    T extends {
        firstName?: string | null;
        familyName?: string | null;
        userName?: string | null;
    },
>(
    user: T | null | undefined,
): string => {
    if (user == null) return 'Null User';
    if (user.firstName != null && user.familyName != null)
        return `${user.firstName} ${user.familyName}`;
    if (user.userName) return user.userName;
    return 'Anonymous';
};

/**
 * @returns The MIME type of the provided dataURL
 */
export const getMimeFromDataURL = (url: string) =>
    url.substring(url.indexOf(':') + 1, url.indexOf(';'));

export const mimeToExtension: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
};

const ISODateReceiver =
    /^\d{4}-(0[1-9]|1[0-2])-([12]\d|0[1-9]|3[01])([T\s](([01]\d|2[0-3]):[0-5]\d|24:00)(:[0-5]\d([.,]\d+)?)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?$/;

/**
 * A Date reciever to add to JSON.parse in order to convert ISO Formatted strings into Date objects.
 *
 * Usage:
 * ```javascript
 * const obj = {d: new Date()}
 * const str = JSON.stringify(obj)
 * // Note the dateReceiver added to JSON.parse
 * const decoded = JSON.parse(str, dateReceiver)
 * console.log(decoded.d.getTime())
 * ```
 */
export const dateReceiver = (key: any, value: any) => {
    if (typeof value === 'string' && ISODateReceiver.exec(value))
        return new Date(value);

    return value;
};

/**
 * @param url A string containing a DataURL
 * @returns A Blob object with the data from the DataURL
 */
export const dataURLtoBlob = (url: string) => {
    const splitData = url.split(',');
    const byteString = Buffer.from(splitData[1], 'base64').toString('base64');
    const mimeString = splitData[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
};

/**
 * Returns a tileGrid for TiledLayer
 * @param crs: The crs of the tiled layer
 * @param levels: The number of levels
 * @param tileSize: The size of each tile; assumes square tiles
 */
export const getTileGrid = (
    crs = MAP_PROJECTION.getCode(),
    extent: any = null,
    levels = 22,
    tileSize = 1024,
) => {
    const resolutions = new Array(levels);
    const projection = getProjection(crs)!;
    const projectionExtent = projection.getExtent();
    const size = getWidth(projectionExtent) / tileSize;

    for (let z = 0; z < levels; z += 1) resolutions[z] = size / 2 ** z;

    const tileGridArgs = {
        minZoom: 0,
        extent: undefined,
        resolutions,
        tileSize,
        origin: getTopLeft(projectionExtent),
    };
    if (extent) tileGridArgs.extent = extent;
    return new TileGrid(tileGridArgs);
};

export const getFormattedLocalDateTime = () => {
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}`;
};

export const convertToPascalCase = (text: string) => {
    return text.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
        index === 0 ? word.toUpperCase() : word.toLowerCase(),
    );
};

/**
 * Returns  true if any of the possibleMatches match the
 * searchTerm return false for anything else.
 * @param possibleMatches: The array to search
 * @param searchTerm: The search term to search for.
 * Could add basic indexing to this via new Map -> return id - not implenented
 */
export const searchIn = (
    possibleMatches: (string | undefined)[],
    searchTerm: string,
) => {
    for (const possibleMatch of possibleMatches) {
        if (
            possibleMatch &&
            possibleMatch.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
        )
            return true;
    }
    return false;
};

export const scaleFromCenter = (extent: number[], value: number) => {
    // patched from openlayers undocumented + broken api scaleFromCenter
    const deltaX = (getWidth(extent) * value) / 2;
    const deltaY = (getHeight(extent) * value) / 2;
    const [centerX, centerY] = getCenter(extent);
    return [
        centerX - deltaX,
        centerY - deltaY,
        centerX + deltaX,
        centerY + deltaY,
    ];
};

export const computeExtentConformingToAspectRatio = (
    aspectRatioToConformTo: number | null,
    extent: number[],
    offset = 1.1,
) => {
    if (aspectRatioToConformTo === null) {
        return extent;
    }

    const mapSize = [
        getWidth(extent),
        getWidth(extent) / aspectRatioToConformTo,
    ];
    const [deltaX, deltaY] = [mapSize[0] / 2, mapSize[1] / 2];
    const [xCenter, yCenter] = getCenter(extent);

    const extentConformingToAspectRatio = [
        xCenter - deltaX,
        yCenter - deltaY,
        xCenter + deltaX,
        yCenter + deltaY,
    ];

    const deltaAspectRatio = Math.max(
        (getWidth(extent) - getWidth(extentConformingToAspectRatio)) / 2,
        (getHeight(extent) - getHeight(extentConformingToAspectRatio)) / 2,
        0,
    );

    if (getHeight(extent) > getWidth(extent)) {
        return scaleFromCenter(
            [
                extentConformingToAspectRatio[0] -
                    deltaAspectRatio * aspectRatioToConformTo,
                extentConformingToAspectRatio[1] - deltaAspectRatio,
                extentConformingToAspectRatio[2] +
                    deltaAspectRatio * aspectRatioToConformTo,
                extentConformingToAspectRatio[3] + deltaAspectRatio,
            ],
            offset,
        );
    } else {
        return scaleFromCenter(
            [
                extentConformingToAspectRatio[0] - deltaAspectRatio,
                extentConformingToAspectRatio[1] -
                    deltaAspectRatio * aspectRatioToConformTo,
                extentConformingToAspectRatio[2] + deltaAspectRatio,
                extentConformingToAspectRatio[3] +
                    deltaAspectRatio * aspectRatioToConformTo,
            ],
            offset,
        );
    }
};
